import React, {Fragment, useState} from 'react';
import {arrayOf, bool, func, number, object, string} from 'prop-types';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {injectIntl, intlShape} from 'react-intl';
import {addIndex, map, path, pathOr} from 'ramda';

import {DATE_TYPE_DATETIME, propTypes} from '../../../../util/types';
import {ensureTransaction} from '../../../../util/data';
import {moment} from '../../../../util/dates';
import {
  getBookingDatesFromTx,
  getUserTxRole,
  txIsEnquired,
  txIsPaymentExpiredAfterEnquiry,
} from '../../../../util/transactions';
import {createSlug} from '../../../../util/urlHelpers';
import {manageDisableScrolling} from '../../../../ducks/UI.duck';
import {getMarketplaceEntities} from '../../../../ducks/marketplaceData.duck';
import {fetchMoreMessages, sendMessage, sendReview} from '../../../../ducks/transaction.duck';
import {IconArrowNavigation} from '../../../../components/Icons';
import {
  ActivityFeed,
  BookingTimeInfo,
  Error,
  IconSpinner,
  NamedLink,
  ReviewModal,
  UserDisplayName,
} from '../../../../components';
import {SendMessageForm} from '../../../../forms';

import {getSendingMessageNotAllowed, getTxType} from '../../InboxPage.Helpers';
import ActionButton from './ActionButton/ActionButton';
import css from './ActivityFeedSection.module.css';
import BoostYourListings from "../../../../components/BoostYourListings";

const ActivityFeedSection = (props) => {
  const {
    className,
    currentUser,
    fetchMessagesError,
    fetchMessagesInProgress,
    fetchTransactionError,
    fetchTransactionInProgress,
    isMobile,
    isTablet,
    messages,
    oldestMessagePageFetched,
    onManageDisableScrolling,
    onSendMessage,
    onSendReview,
    onShowMoreMessages,
    sendMessageError,
    sendMessageInProgress,
    sendReviewError,
    sendReviewInProgress,
    setShowDetails,
    showDetails,
    totalMessagePages,
    transaction,
    intl,
  } = props;
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [reviewSubmitted, setReviewSubmitted] = useState(false);
  const [sendMessageFormFocused, setSendMessageFormFocused] = useState(false);
  const history = useHistory();
  const onOpenReviewModal = () => setIsReviewModalOpen(true);
  const onCloseReviewModal = () => setIsReviewModalOpen(false);
  const onSendMessageFormFocus = () => setSendMessageFormFocused(true);
  const onSendMessageFormBlur = () => setSendMessageFormFocused(false);
  const scrollToMessage = (messageId) => {
    const selector = `#msg-${messageId.uuid}`;
    const el = document.querySelector(selector);
    if (el) {
      el.scrollIntoView({
        block: 'start',
        behavior: 'smooth',
      });
    }
  };
  const onMessageSubmit = (values, form) => {
    const message = values.message ? values.message.trim() : null;
    const ensuredTransaction = ensureTransaction(transaction);

    if (!message) return;

    onSendMessage(ensuredTransaction.id, message)
      .then((messageId) => {
        form.reset();
        scrollToMessage(messageId);
      })
      .catch(() => {
      });
  };
  const onSubmitReview = (values) => {
    const ensuredTransaction = ensureTransaction(transaction);
    const transactionRole = getUserTxRole(currentUser.id, ensuredTransaction);
    const {reviewRating, reviewContent} = values;
    const rating = Number.parseInt(reviewRating, 10);
    onSendReview(transactionRole, ensuredTransaction, rating, reviewContent)
      .then(() => {
        setIsReviewModalOpen(false);
        setReviewSubmitted(true);
      })
      .catch(() => {
      });
  };
  const transactionUuid = path(['id', 'uuid'], transaction);
  const txType = getTxType(transaction, currentUser);
  const isOrder = txType === 'order';
  const isEnquire = transactionUuid && txIsEnquired(transaction);
  const isExpired = transactionUuid && txIsPaymentExpiredAfterEnquiry(transaction);
  const {customer, booking, listing, provider} = transaction;
  const otherUser = isOrder ? provider : customer;
  const hasOlderMessages = totalMessagePages > oldestMessagePageFetched;
  const hasErrors = fetchTransactionError || fetchMessagesError;
  const isLoading = fetchTransactionInProgress || fetchMessagesInProgress;
  const sendingMessageNotAllowed = getSendingMessageNotAllowed(transaction, intl);
  const bookingDate =
    path(['attributes', 'end'], booking) ||
    path(['attributes', 'protectedData', 'date'], transaction);
  const bookingDates = getBookingDatesFromTx(transaction);
  const dateDiff = bookingDate && moment().diff(moment(bookingDate), 'days');
  const outdated = dateDiff > 10;
  const listingUuid = path(['id', 'uuid'], listing);
  const listingTitle = pathOr('', ['attributes', 'title'], listing);
  const slug = createSlug(listingTitle);
  const outdatedLinkText = intl.formatMessage({id: 'InboxPage.ActivityFeedSection.outdatedLink'});
  const errorText = intl.formatMessage({id: 'InboxPage.ActivityFeedSection.error'});
  const sendMessagePlaceholder = intl.formatMessage({id: 'InboxPage.sendMessagePlaceholder'});
  const otherUserDisplayName = <UserDisplayName user={otherUser} intl={intl}/>;

  return (
    <section className={className}>
      <div className={css.header}>
        {isMobile && (
          <button className={css.backButton} onClick={() => history.goBack()}>
            <IconArrowNavigation className={css.iconArrow} direction="left"/>
          </button>
        )}
        {transactionUuid && (
          <Fragment>
            <div className={css.headingWrapper}>
              <h2 className={css.heading}>{otherUserDisplayName}</h2>
              {isMobile && bookingDates && (
                <div className={css.bookingInfoWrapper}>
                  <BookingTimeInfo dateType={DATE_TYPE_DATETIME} tx={transaction} intl={intl}/>
                </div>
              )}
            </div>
            {!sendingMessageNotAllowed ? (
              <ActionButton
                className={css.actionButton}
                handleShowDetails={setShowDetails}
                intl={intl}
                isEnquire={isEnquire}
                isExpired={isExpired}
                isMobile={isMobile}
                isOrder={isOrder}
                listingSlug={slug}
                listingUuid={listingUuid}
                showDetails={showDetails}
                transactionUuid={transactionUuid}
              />
            ) : null}
          </Fragment>
        )}
      </div>
      <div className={css.content}>
        {isLoading && <IconSpinner/>}
        {!isLoading && hasErrors && <Error className={css.error} errorText={errorText}/>}
        {!isLoading && transactionUuid && (
          <div className={css.wrapper}>
            <div className={css.activityFeedContainer}>
              <div className={css.activityFeedWrapper}>
                {!isOrder && (
                  <div className={css.boostYourListingsContainer}>
                    <BoostYourListings/>
                  </div>
                )}
                <ActivityFeed
                  className={css.activityFeed}
                  messages={messages}
                  transaction={transaction}
                  currentUser={currentUser}
                  hasOlderMessages={hasOlderMessages && !fetchMessagesInProgress}
                  onOpenReviewModal={onOpenReviewModal}
                  onShowOlderMessages={onShowMoreMessages}
                  fetchMessagesInProgress={fetchMessagesInProgress}
                />
              </div>
            </div>
            {sendMessageError?.name === 'isEvent' && (
              <div className={css.bypassingErrorPadding}>
                <div className={css.bypassingErrorContainer}>
                  <div className={css.bypassTitle}>
                    {intl.formatMessage({id: 'InboxPage.LookForEventTitle'})}
                  </div>
                  <div className={css.bypassDescription}>
                    {intl.formatMessage({id: 'InboxPage.LookForEventDescription'})}
                  </div>
                  <NamedLink
                    className={css.bookLink}
                    name="ListingPage"
                    params={{id: listingUuid, slug}}
                    to={{ search: '?adults=10' }}
                  >
                    {intl.formatMessage({id: 'InboxPage.LookForEventCTA'})}
                  </NamedLink>
                </div>
              </div>
            )}
            {sendMessageError?.name === 'bypassing' && (
              <div className={css.bypassingErrorPadding}>
                <div className={css.bypassingErrorContainer}>
                  <div className={css.bypassTitle}>
                    {intl.formatMessage({id: 'InboxPage.BypassWarningTitle'})}
                  </div>
                  <div className={css.bypassDescription}>
                    {intl.formatMessage({id: 'InboxPage.BypassWarningDescription'})}
                  </div>
                </div>
              </div>
            )}
            <div className={css.sendMessageFormContainer}>
              {sendingMessageNotAllowed ? (
                <div className={css.sendingMessageNotAllowed}>
                  <span className={css.notAllowedText}>{sendingMessageNotAllowed}</span>
                  {outdated && (
                    <NamedLink
                      className={css.outdatedLink}
                      name="ListingPage"
                      params={{id: listingUuid, slug}}
                    >
                      {outdatedLinkText}
                    </NamedLink>
                  )}
                </div>
              ) : (
                <SendMessageForm
                  formId="InboxPage.SendMessageForm"
                  rootClassName={css.sendMessageForm}
                  messagePlaceholder={sendMessagePlaceholder}
                  inProgress={sendMessageInProgress}
                  isMobile={isMobile || isTablet}
                  sendMessageError={sendMessageError}
                  onFocus={onSendMessageFormFocus}
                  onBlur={onSendMessageFormBlur}
                  onSubmit={onMessageSubmit}
                />
              )}
            </div>
          </div>
        )}
      </div>
      <ReviewModal
        id="ReviewModal"
        isOpen={isReviewModalOpen}
        onCloseModal={onCloseReviewModal}
        onManageDisableScrolling={onManageDisableScrolling}
        onSubmit={onSubmitReview}
        revieweeName={otherUserDisplayName}
        reviewSent={reviewSubmitted}
        sendReviewInProgress={sendReviewInProgress}
        sendReviewError={sendReviewError}
      />
    </section>
  );
};

ActivityFeedSection.defaultProps = {
  currentUser: null,
  fetchMessagesError: null,
  fetchTransactionError: null,
  oldestMessagePageFetched: 0,
  sendMessageError: null,
  sendReviewError: null,
  totalMessagePages: 0,
  transaction: {},
};

ActivityFeedSection.propTypes = {
  className: string.isRequired,
  currentUser: propTypes.currentUser,
  fetchMessagesError: propTypes.error,
  fetchMessagesInProgress: bool.isRequired,
  fetchTransactionError: propTypes.error,
  fetchTransactionInProgress: bool.isRequired,
  isMobile: bool.isRequired,
  isTablet: bool.isRequired,
  messages: arrayOf(propTypes.message).isRequired,
  oldestMessagePageFetched: number,
  onManageDisableScrolling: func.isRequired,
  onSendMessage: func.isRequired,
  onSendReview: func.isRequired,
  onShowMoreMessages: func.isRequired,
  sendMessageError: propTypes.error,
  sendMessageInProgress: bool.isRequired,
  sendReviewError: propTypes.error,
  sendReviewInProgress: bool.isRequired,
  setShowDetails: func.isRequired,
  showDetails: bool.isRequired,
  totalMessagePages: number,
  transaction: object,
  intl: intlShape.isRequired,
};

const mapIndexed = addIndex(map);

const mapStateToProps = (state) => {
  const {
    fetchMessagesError,
    fetchMessagesInProgress,
    fetchTransactionError,
    fetchTransactionInProgress,
    messages: conversation,
    oldestMessagePageFetched,
    sendMessageError,
    sendMessageInProgress,
    sendReviewError,
    sendReviewInProgress,
    totalMessagePages,
    transactionRef,
  } = state.transaction;
  const transaction = getMarketplaceEntities(state, transactionRef ? [transactionRef] : [])[0];
  const providerOfferInvitations = pathOr(
    [],
      ['attributes', 'metadata', 'providerOfferInvitations'],
    transaction,
  );
  const getSystemMessages = (
    { createdAt, ...proposal },
    index,
  ) => ({
    type: 'message',
    id: `proposal-invitation-by-provider-${index}`,
    attributes: {
      content: `__proposal:${JSON.stringify(proposal)}:proposal__`,
      createdAt: moment(createdAt).toDate(),
    },
    system: true,
  });
  const systemMessages = mapIndexed(getSystemMessages, providerOfferInvitations);
  const messages = [...systemMessages, ...conversation].sort((a, b) => {
    const dateA = new Date(a.createdAt).getTime();
    const dateB = new Date(b.createdAt).getTime();
    return dateA > dateB ? -1 : 1;
  });

  return {
    fetchMessagesError,
    fetchMessagesInProgress,
    fetchTransactionError,
    fetchTransactionInProgress,
    messages,
    oldestMessagePageFetched,
    sendMessageError,
    sendMessageInProgress,
    sendReviewError,
    sendReviewInProgress,
    totalMessagePages,
    transaction,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    onManageDisableScrolling: (componentId, disableScrolling) =>
      dispatch(manageDisableScrolling(componentId, disableScrolling)),
    onSendMessage: (txId, message) => dispatch(sendMessage(txId, message)),
    onSendReview: (role, tx, reviewRating, reviewContent) =>
      dispatch(sendReview(role, tx, reviewRating, reviewContent)),
    onShowMoreMessages: (txId) => dispatch(fetchMoreMessages(txId)),
  };
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  injectIntl
)(ActivityFeedSection);
